/* Corner close-up: shows the empty frame, or the customer's own artwork (upload or chosen print, in
   "With artwork" mode) inside it. No labels or messages on the image: if anything can't be drawn,
   the plain corner photo stays. Reuses the artwork image already loaded by the existing preview.
   Drawn from a plain copy of the photo (same file, from the browser cache): a srcset <img> reports naturalWidth
   divided by the screen density (about 390 instead of 1344 on a phone), which made the drawing, frame included,
   low resolution on mobile. */
(function () {
  if (customElements.get('ess-corner-photo')) return;
  class CornerPhoto extends HTMLElement {
    connectedCallback() {
      this.photo = this.querySelector('.ess-corner-original');
      this.canvas = this.querySelector('canvas');
      this.placement = JSON.parse(this.querySelector('[data-corner-placement]').textContent);
      this.variants = JSON.parse(this.querySelector('[data-corner-variants]').textContent);
      this.variant = this.variant || this.dataset.selectedVariant;
      this.near = false;
      this.lastKey = '';
      this.artIds = new WeakMap();
      this.nextArtId = 0;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.photo.addEventListener('load', () => this.draw(), options);
      this.photo.addEventListener('error', () => this.plain(), options);
      document.addEventListener('ess:corner-artwork', () => this.draw(), options);
      document.addEventListener('variant:change', event => {
        if (event.target.id !== this.dataset.form && event.detail?.formId !== this.dataset.form) return;
        this.variant = event.detail?.variant?.id || '';
        this.draw();
      }, { ...options, capture: true });
      // Also cover native form changes; Impact's asynchronous variant event is authoritative.
      document.addEventListener('change', event => {
        const form = document.getElementById(this.dataset.form);
        if (event.target.form !== form || event.target.name !== 'id') return;
        this.variant = event.target.value; this.draw();
      }, options);
      this.observer = new IntersectionObserver(entries => {
        this.near = entries.some(entry => entry.isIntersecting);
        if (this.near) this.draw();
      }, { rootMargin: '300px' });
      this.observer.observe(this);
    }
    disconnectedCallback() {
      this.abort?.abort(); this.observer?.disconnect();
    }
    plain() { this.canvas.hidden = true; this.lastKey = ''; }
    full() {
      // the photo at its real pixel size: same URL as the <img> (cached), without srcset density scaling
      const src = this.photo.currentSrc || this.photo.src;
      if (!src) return null;
      if (this.fullImg && this.fullImg.__src === src) return this.fullImg.complete && this.fullImg.naturalWidth ? this.fullImg : null;
      const im = new Image(); im.__src = src; im.decoding = 'async';
      im.onload = () => this.draw(); im.onerror = () => { if (this.fullImg === im) this.fullImg = null; };
      im.src = src; this.fullImg = im;
      return im.complete && im.naturalWidth ? im : null;
    }
    draw() {
      if (!this.isConnected || !this.near || !this.photo.complete || !this.photo.naturalWidth || !window.ESSCornerArtwork) return;
      const state = window.ESSCornerArtworkState;
      const art = state?.image && state.mode === 'art' ? state.image : null;
      if (!art) { this.plain(); return; }
      const size = window.ESSCornerArtwork.parseSizeMm(this.variants[String(this.variant)] || '');
      if (!size || !size.every(n => Number.isFinite(n) && n > 0)) { this.plain(); return; }
      if (!art.complete || !art.naturalWidth) return;
      const photo = this.full(); if (!photo) return;   // drawn when the full-size copy has loaded
      if (!this.artIds.has(art)) this.artIds.set(art, ++this.nextArtId);
      const key = [this.artIds.get(art), size.join('x'), photo.__src, photo.naturalWidth].join('|');
      if (key === this.lastKey) return;
      try {
        window.ESSCornerArtwork.render(this.canvas, photo, art, this.placement, size[0], size[1]);
        this.lastKey = key;
        this.canvas.hidden = false;
        this.dataset.renderedSize = size.join('x');
      } catch (error) { this.plain(); }
    }
  }
  customElements.define('ess-corner-photo', CornerPhoto);
})();
